import * as React from 'react';
import {
    ContextualMenu,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    IContextualMenuProps,
    PrimaryButton,
} from '@fluentui/react';
import { getMwstCode, useKunde } from 'api/kunde';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import moment from 'moment-timezone';
import { cloneDeep, isEqual } from 'lodash';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import {
    addService,
    Service,
    copyService,
    deleteService,
    getDefaultService,
    recalculateService,
    sendService,
    updateService,
    useService,
} from 'api/service';
import { useHistory } from 'react-router-dom';
import { formStyles } from 'styles/formStyles';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { Timestamps } from 'formComponents/Timestamps';
import { BetreuerById } from 'formComponents/lookups/BetreuerById';
import { FormTextarea, FormTextareaOptions } from 'formComponents/FormTextarea';
import { Textbaustein } from 'formComponents/lookups/Textbaustein';
import { ServiceStatus } from 'formComponents/lookups/ServiceStatus';
import { ServiceTyp } from 'formComponents/lookups/ServiceTyp';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import { KundeSelect } from 'components/Kunde/KundeSelect';
import { createKundeUrl, createRecordUrl, formatMoney } from 'utils/formatter';
import { ServicePosition, useServicePositionList } from 'api/servicePosition';
import { AnsprechpartnerSelect } from 'components/Kunde/Ansprechpartner/AnsprechpartnerSelect';
import { Skontodefinition } from 'formComponents/lookups/Skontodefinition';
import { getApiUrl } from 'api/useApi';
import { addAuftragFromService } from 'api/auftrag';
import { SendMail } from 'components/SendMail/SendMail';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { ServiceStatusLabel } from 'utils/labels';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { OpenLinkIcon } from 'formComponents/OpenLinkIcon/OpenLinkIcon';
import { logDifference } from 'utils/helper';
import { SmRefType } from 'api/attachment';
import { AraRegelung } from 'formComponents/lookups/AraRegelung';
import { useTerminList } from 'api/termin';
import { ServicePositionen } from './ServicePositionen';
import { ServicePositionenSelect } from './ServicePositionenSelect';
import { LinkedAttachmentList } from '../History/LinkedAttachmentList';
import { AddAttachments } from '../History/AddAttachments';
import { ServiceSign } from './ServiceSign';
import { ZusatzadresseTyp } from '../../api/zusatzadresse';
import { ZusatzadresseSelect } from '../Kunde/Zusatzadresse/ZusatzadresseSelect';

export interface ServiceEditData {
    kNr?: number;
    sNr: string;
}

interface ServiceEditProps {
    defaultKNr?: number;
    sNr: string; // 'neu' = new
    onClose: (refetch: boolean) => void;
    onReopen: (sNr: string) => void;
}

type ButtonAction = 'reopen' | 'print' | 'printOnBriefpapier' | 'send' | 'copy' | 'createAuftrag' | 'delete' | 'sign';

const allowedStatus = [0, 1, 2, 3];

export const ServiceEdit: React.FC<ServiceEditProps> = ({ defaultKNr, sNr, onClose, onReopen }) => {
    const history = useHistory();

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const isMounted = useIsMounted();

    const [refetchOnClose, setRefetchOnClose] = React.useState(false);

    const [service, setService] = React.useState<Service | null>(getDefaultService(defaultKNr));
    const dbService = useService(
        {
            nr: sNr,
            withKunde: true,
        },
        {
            enabled: sNr !== 'neu',
            onSuccess: (loadedService) => {
                setService(cloneDeep(loadedService));
                setSavingState(SavingState.notChanged);
            },
        },
    );

    const kunde = useKunde(
        {
            nr: service?.kNr,
            withZusatzadresseList: true,
            withAttachmentList: true,
        },
        {
            enabled: service?.kNr !== undefined,
            onSuccess: (newKunde) => {
                if (sNr === 'neu' && service) {
                    const lieferAdr = newKunde?.zusatzadresseList?.find((za) => za.typ === 'L' && za.std && !za.archiv);

                    setService({
                        ...service,
                        betreuer: newKunde?.betreuer || 0,
                        telBetreuer: newKunde?.telBetreuer || 0,
                        zahlBed: newKunde?.zahlZiel || service.zahlBed,
                        lieferAdrNr: lieferAdr?.nr || 0,
                        mwstCode: getMwstCode(newKunde),
                        ara: newKunde?.ara || 0,
                    });
                }
            },
        },
    );

    const servicePositionList = useServicePositionList(
        {
            sNr,
        },
        { enabled: sNr !== 'neu' },
    );

    const kNrArtikelGruppeNrAndArtikelNrList = React.useMemo(() => {
        const list: string[] = [];
        if (service && servicePositionList.data) {
            (servicePositionList.data || []).forEach((p) => {
                if (p.artikelGruppeNr && p.artikelNr) {
                    list.push(service.kNr + '|' + p.artikelGruppeNr + '|' + p.artikelNr);
                }
            });
        }

        return list;
    }, [service, servicePositionList]);

    const wiedervorlagen = useTerminList({
        terminStatus: 0,
        terminArt: 'W',
        kNrArtikelGruppeNrAndArtikelNrList,
    });

    const [showCopy, setShowCopy] = React.useState(false);
    const [showSend, setShowSend] = React.useState(false);
    const [showDelete, setShowDelete] = React.useState(false);
    const [showSign, setShowSign] = React.useState(false);
    const [showCreateAuftrag, setShowCreateAuftrag] = React.useState(false);
    const [changeStatusValue, setChangeStatusValue] = React.useState<number | null>(null);
    const [transferPositionen, setTransferPositionen] = React.useState<ServicePosition[] | null>(null);
    const [showAddAttachments, setShowAddAttachments] = React.useState(false);

    React.useEffect(() => {
        if (
            [SavingState.notSaved, SavingState.notChanged].includes(savingState) &&
            !dbService.isLoading &&
            !dbService.isFetching &&
            service !== null
        ) {
            if (!isEqual(dbService.data, service)) {
                setSavingState(SavingState.notSaved);
                logDifference('service', service, dbService.data);
            } else if (savingState !== SavingState.notChanged) {
                setSavingState(SavingState.notChanged);
            }
        }
    }, [savingState, dbService, service]);

    const readOnly = React.useMemo(() => {
        return service?.status !== 0;
    }, [service?.status]);

    const validate = React.useCallback((): boolean => {
        if (service?.kNr === undefined) {
            setValidationError('Bitte wählen Sie einen Kunden aus.');
            return false;
        }
        if (validationError !== null) {
            setValidationError(null);
        }

        return true;
    }, [service, validationError]);

    const handleButtonAction = React.useCallback(
        (action?: ButtonAction) => {
            if (!service?.nr || !action) {
                return;
            }

            switch (action) {
                case 'reopen':
                    onReopen(service.nr);
                    break;
                case 'print':
                    window.open(getApiUrl('service/pdf') + '?nr=' + service.nr);
                    break;
                case 'printOnBriefpapier':
                    window.open(getApiUrl('service/pdf') + '?nr=' + service.nr + '&briefpapier=true');
                    break;
                case 'send':
                    setShowSend(true);
                    break;
                case 'copy':
                    setShowCopy(true);
                    break;
                case 'delete':
                    setShowDelete(true);
                    break;
                case 'createAuftrag':
                    setTransferPositionen(
                        (servicePositionList?.data || []).map((ap) => ({
                            ...ap,
                            auNr: '',
                            offen: 0,
                            transfer: true,
                            transferMenge: ap.posMenge,
                            lArt: '',
                        })),
                    );
                    setShowCreateAuftrag(true);
                    break;
                case 'sign':
                    setShowSign(true);
                    break;
                default:
                    break;
            }
        },
        [service, onReopen, servicePositionList?.data],
    );

    const save = React.useCallback(
        (onFinish?: (nr: string) => void, status?: number) => {
            if (!service) {
                return;
            }

            const updatedService: Service = status === undefined ? service : { ...service, status };

            if (savingState !== SavingState.saving) {
                if (validate()) {
                    setSavingState(SavingState.saving);
                    if (sNr === 'neu') {
                        addService(updatedService)
                            .then((result) => {
                                setSavingState(SavingState.saved);
                                setRefetchOnClose(true);
                                if (isMounted) {
                                    onReopen(result.newNr);
                                }
                                if (onFinish) {
                                    onFinish(result.newNr);
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                setSavingState(SavingState.error);
                            });
                    } else {
                        updateService(updatedService)
                            .then(() => {
                                setRefetchOnClose(true);
                                if (isMounted) {
                                    dbService.refetch().then();
                                    setSavingState(SavingState.notChanged);
                                }
                                if (onFinish) {
                                    onFinish(service.nr);
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                setSavingState(SavingState.error);
                            });
                    }
                }
            }
        },
        [dbService, service, sNr, isMounted, onReopen, savingState, validate],
    );

    const statusButtonMenuProps: IContextualMenuProps = React.useMemo(() => {
        const items = allowedStatus.map((s) => ({
            key: s.toString(),
            text: ServiceStatusLabel[s],
            onClick: () => setChangeStatusValue(s),
        }));

        return { items };
    }, []);

    const actionButtonMenuProps: IContextualMenuProps = React.useMemo(() => {
        const items = [
            {
                key: 'print',
                text: 'Drucken mit Briefkopf',
                iconProps: { iconName: 'Print' },
                onClick: () => handleButtonAction('print'),
            },
        ];

        if (sNr !== 'neu' && service?.status === 0) {
            items.push({
                key: 'delete',
                text: 'Löschen',
                iconProps: { iconName: 'Delete' },
                onClick: () => handleButtonAction('delete'),
            });
        }

        return { items };
    }, [sNr, service?.status, handleButtonAction]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    const renderTextField = (column: keyof Service, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(service?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (service) {
                    setService({
                        ...service,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderTextarea = (column: keyof Service, label: string, options: FormTextareaOptions = {}) => (
        <FormTextarea
            value={service?.[column] as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (service) {
                    setService({
                        ...service,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderCheckbox = (column: keyof Service, label: string) => (
        <Checkbox
            className={formStyles.checkboxInsideFormAligned}
            label={label}
            disabled={savingState === SavingState.saving || readOnly}
            checked={service?.[column] as boolean}
            onChange={(event, checked) => {
                if (service) {
                    setService({
                        ...service,
                        [column]: checked || false,
                    });
                }
            }}
        />
    );

    const renderMa = (column: keyof Service, label: string) => (
        <BetreuerById
            value={service?.[column] as number}
            disabled={savingState === SavingState.saving || readOnly}
            label={label}
            allowEmpty
            menuPlacement="top"
            onChange={(newValue): void => {
                if (service) {
                    setService({
                        ...service,
                        [column]: newValue,
                    });
                }
            }}
        />
    );

    const renderTextbaustein = (
        column: keyof Service,
        label: string,
        kz: number,
        allowEmpty: boolean,
        menuPlacement: 'bottom' | 'top' = 'bottom',
    ) => (
        <Textbaustein
            kz={kz}
            value={service?.[column] as number}
            disabled={savingState === SavingState.saving || readOnly}
            label={label}
            allowEmpty={allowEmpty}
            menuPlacement={menuPlacement}
            onChange={(newValue): void => {
                if (service) {
                    setService({
                        ...service,
                        [column]: parseInt(newValue, 10) || 0,
                    });
                }
            }}
        />
    );

    const renderDatePicker = (column: keyof Service, label: string, required = false, defaultNow = false) => {
        return (
            <FormDatePicker
                label={label}
                savingState={savingState}
                value={(service?.[column] as string) || (defaultNow ? moment().toISOString() : undefined)}
                onChange={(newValue) => {
                    if (service) {
                        setService({
                            ...service,
                            [column]: newValue,
                        });
                    }
                }}
                options={{
                    required,
                    disabled: readOnly,
                }}
            />
        );
    };

    const renderServiceStatus = () => (
        <ServiceStatus
            value={service?.status?.toString() || '0'}
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (service) {
                    setService({
                        ...service,
                        status: parseInt(newValue, 10),
                    });
                }
            }}
        />
    );

    const renderServiceTyp = () => (
        <ServiceTyp
            value={service?.typ?.toString() || '0'}
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (service) {
                    setService({
                        ...service,
                        typ: parseInt(newValue, 10),
                    });
                }
            }}
        />
    );

    const renderKundeSelect = () => (
        <KundeSelect
            label="Kunde"
            kNr={service?.kNr}
            name={kunde.data?.name1}
            disabled={savingState === SavingState.saving || readOnly /* || sNr !== 'neu' */}
            onChange={(newKNr): void => {
                if (service && service.kNr !== newKNr) {
                    setService({
                        ...service,
                        kNr: newKNr,
                        kaNr: 0,
                        kaName: '',
                        lieferAdrNr: 0,
                        lieferAdr: undefined,
                    });
                }
            }}
        />
    );

    const renderAnsprechpartner = () => (
        <AnsprechpartnerSelect
            label="Ansprechpartner"
            kNr={service?.kNr}
            kaNr={service?.kaNr}
            kaName={service?.kaName}
            disabled={savingState === SavingState.saving || readOnly}
            allowEmpty
            onChange={(newKaNr, newKaName): void => {
                if (service) {
                    setService({
                        ...service,
                        kaNr: newKaNr || 0,
                        kaName: newKaName || '',
                    });
                }
            }}
        />
    );

    const renderZusatzadresse = (
        column: 'lieferAdrNr',
        subRecord: 'lieferAdr',
        label: string,
        typ: ZusatzadresseTyp,
    ) => (
        <ZusatzadresseSelect
            label={label}
            kNr={service?.kNr}
            zaNr={service?.[column] || 0}
            typ={typ}
            zusatzadresse={service?.[subRecord]}
            disabled={savingState === SavingState.saving || readOnly}
            allowEmpty
            onChange={(newZaNr, newZusatzadresse): void => {
                if (service) {
                    setService({
                        ...service,
                        [column]: newZaNr || 0,
                        [subRecord]: newZusatzadresse,
                    });
                }
            }}
        />
    );

    const renderSkontodefinition = () => (
        <Skontodefinition
            value={service?.zahlBed || 0}
            label="Zahlungsbedingungen"
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (service) {
                    setService({
                        ...service,
                        zahlBed: newValue,
                    });
                }
            }}
        />
    );

    const renderAraRegelung = () => (
        <AraRegelung
            value={service?.ara || 0}
            label="ARA Regelung"
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (service) {
                    setService({
                        ...service,
                        ara: newValue,
                    });
                }
            }}
        />
    );

    /*
    const validationWarning =
        service?.ara !== 1 && // "Die Ware ist nicht entpflichtet"
        servicePositionList.data &&
        !servicePositionList.data.find((ap) => ap.artikelGruppeNr === 97)
            ? 'Achtung: Es ist noch kein Reclay Artikel eingetragen.'
            : null;
     */

    return (
        <>
            {routerConfirmMessage.render()}
            <Dialog
                hidden={false}
                title={`Service ${service?.nr || ''} - ${kunde.data?.name1 || '...'} ${kunde.data?.name2 || ''} ${
                    kunde.data?.nr ? '(' + kunde.data?.nr + ')' : ''
                }`}
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth="95%"
                maxWidth="95%"
                onDismiss={() => {
                    onClose(refetchOnClose);
                }}
            >
                <form className={formStyles.form}>
                    <div className={formStyles.threeColumnsBlock}>
                        <div className={formStyles.firstColumn}>
                            <div className={formStyles.gridCell}>
                                {renderKundeSelect()}
                                {kunde.data ? <OpenLinkIcon link={createKundeUrl(kunde.data.nr.toString())} /> : null}
                            </div>
                            <div className={formStyles.gridCell}>{renderAnsprechpartner()}</div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('nr', 'Service-Nr.', { maxLength: 10, disabled: true })}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('auNr', 'Auftrag-Nr.', { maxLength: 10 })}
                                    {service?.auNr ? (
                                        <OpenLinkIcon link={createRecordUrl('auftraege', defaultKNr, service?.auNr)} />
                                    ) : null}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderDatePicker('datum', 'Datum', true, true)}
                                </div>
                                <div className={formStyles.gridCell}>{renderMa('betreuer', 'Betreuer')}</div>
                                <div className={formStyles.gridCell}>{renderMa('telBetreuer', 'Tel.-Betreuer')}</div>
                            </div>
                            <div className={formStyles.gridCell}>
                                {kunde.data?.attachmentList && sNr !== 'neu' && (
                                    <LinkedAttachmentList
                                        kNr={kunde.data.nr}
                                        smRefType={SmRefType.service}
                                        smRef={sNr}
                                        attachmentList={kunde.data.attachmentList}
                                        refetchKunde={() => kunde.refetch()}
                                        onAdd={() => setShowAddAttachments(true)}
                                    />
                                )}
                            </div>
                        </div>
                        <div className={formStyles.secondColumn}>
                            <div className={formStyles.gridCell}>{renderTextarea('txt', 'Text')}</div>
                            <div className={formStyles.gridCell}>
                                {renderZusatzadresse('lieferAdrNr', 'lieferAdr', 'Lieferadresse', 'L')}
                            </div>
                        </div>

                        <div className={formStyles.thirdColumn}>
                            <div className={formStyles.gridCell}>{renderServiceStatus()}</div>
                            <div className={formStyles.gridCell}>{renderServiceTyp()}</div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell} />
                                <div className={formStyles.gridCellRight}>
                                    {renderCheckbox('extDoku', 'Ext. Doku.')}
                                </div>
                                {/*
                                <div className={formStyles.gridCell}>{renderCheckbox('teilR', 'Teilrechnung')}</div>
                                */}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('liefBed', 'Lieferbedingung', 11, true)}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('liefZeit', 'Lieferzeit', 14, true)}
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>{renderDatePicker('liefDat', 'Lieferdatum')}</div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('liefKw', 'Liefer-KW', { numeric: true })}
                                </div>
                            </div>
                            <div className={formStyles.gridCell}>
                                {/* renderTextbaustein('zahlBed', 'Zahlungsbedingungen', 12, true, 'top') */}
                                {renderSkontodefinition()}
                            </div>
                            <div className={formStyles.gridCell}>{renderAraRegelung()}</div>
                            <div className={formStyles.gridCell}>
                                {renderTextField('aktText', 'Betreff E-Akt', { maxLength: 100 })}
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell} />
                                <div className={formStyles.gridCell}>
                                    {renderTextField('mwstCode', 'MwSt.', { numeric: true, disabled: true })}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={formStyles.threeColumnsBlock}>
                        <div className={formStyles.firstColumn}>
                            <div className={formStyles.gridCell}>
                                <FormTextarea
                                    value={kunde.data?.bemerkung || ''}
                                    label="Bemerkung (beim Kunden)"
                                    onChange={() => {}}
                                    savingState={savingState}
                                    options={{ disabled: true }}
                                />
                            </div>
                        </div>
                        <div className={formStyles.secondColumn}>
                            <FormTextarea
                                value={kunde.data?.liefBem || ''}
                                label="Lieferbemerkung (beim Kunden)"
                                onChange={() => {}}
                                savingState={savingState}
                                options={{ disabled: true }}
                            />
                        </div>
                    </div>

                    <div>
                        {service && servicePositionList?.data && kunde.data && (
                            <ServicePositionen
                                kunde={kunde.data}
                                service={service}
                                sNr={service.nr}
                                servicePositionen={servicePositionList?.data}
                                wiedervorlagen={wiedervorlagen.data || []}
                                viewMode="long"
                                allowEdit={!readOnly && savingState === SavingState.notChanged}
                                refetch={async () => {
                                    const result = await recalculateService(service);
                                    if (result?.service) {
                                        dbService.refetch().then();
                                        setService({
                                            ...service,
                                            sNetto: result.service.sNetto,
                                            sBrutto: result.service.sBrutto,
                                            sMwst: result.service.sMwst,
                                        });
                                        servicePositionList.refetch().then();
                                        wiedervorlagen.refetch().then();
                                    }

                                    setRefetchOnClose(true);
                                }}
                            />
                        )}
                    </div>

                    {servicePositionList?.data && (
                        <div className={formStyles.threeColumnsBlock}>
                            <div className={formStyles.firstColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe Netto: {formatMoney(service?.sNetto || 0)}
                                </div>
                            </div>
                            <div className={formStyles.secondColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe Brutto: {formatMoney(service?.sBrutto || 0)}
                                </div>
                            </div>
                            <div className={formStyles.thirdColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe MwSt.: {formatMoney(service?.sMwst || 0)}
                                </div>
                            </div>
                        </div>
                    )}

                    <Timestamps
                        createdDate={service?.anlageDat}
                        createdDecimalTime={service?.anlageUhr}
                        createdUser={service?.anlageUser}
                        modifiedDate={service?.updDat}
                        modifiedDecimalTime={service?.updUhr}
                        modifiedUser={service?.updUser}
                    />

                    {validationError && <div className={formStyles.error}>{validationError}</div>}
                    {/* validationWarning && <div className={formStyles.warn}>{validationWarning}</div> */}
                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}

                    <DialogFooter>
                        <DefaultButton
                            className={formStyles.signButton}
                            onClick={() => handleButtonAction('sign')}
                            text="Unterschreiben"
                            disabled={savingState !== SavingState.notChanged || !service?.nr}
                        />
                        <DefaultButton
                            className={formStyles.copyButton}
                            onClick={() => handleButtonAction('copy')}
                            text="Kopieren"
                            disabled={savingState !== SavingState.notChanged || !service?.nr}
                        />
                        <DefaultButton
                            className={formStyles.sendButton}
                            onClick={() => handleButtonAction('send')}
                            text="Senden"
                            disabled={savingState !== SavingState.notChanged || !service?.nr}
                        />
                        <DefaultButton
                            className={formStyles.migrateButton}
                            onClick={() => handleButtonAction('createAuftrag')}
                            text="Auftrag erstellen"
                            disabled={savingState !== SavingState.notChanged || !service?.nr}
                        />
                        <DefaultButton
                            split
                            splitButtonAriaLabel="Status"
                            aria-roledescription="action button"
                            disabled={savingState !== SavingState.notChanged || !service?.nr}
                            text="Status ändern"
                            menuProps={statusButtonMenuProps}
                            onClick={() => {}}
                        />
                        <DefaultButton
                            split
                            splitButtonAriaLabel="Weitere Aktionen"
                            aria-roledescription="action button"
                            disabled={savingState !== SavingState.notChanged || !service?.nr}
                            onClick={() => handleButtonAction('printOnBriefpapier')}
                            text="Drucken"
                            menuProps={actionButtonMenuProps}
                        />

                        {savingState === SavingState.saving ? (
                            <PrimaryButton text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={() => save()}
                                text="Speichern"
                                disabled={savingState === SavingState.notChanged || dbService.isLoading}
                            />
                        )}
                    </DialogFooter>
                </form>
                {(dbService.isLoading || dbService.isFetching || kunde.isLoading || kunde.isFetching || !service) && (
                    <LoadingIndicator />
                )}
            </Dialog>
            {showCopy && (
                <KundeSelect
                    label="Kunde"
                    disabled={savingState === SavingState.saving || readOnly || sNr !== 'neu'}
                    dialogOnly
                    initFilterKNr={service?.kNr}
                    onChange={async (newKNr) => {
                        if (service && newKNr) {
                            const { newNr } = await copyService(newKNr, service, servicePositionList?.data);
                            setShowCopy(false);
                            onReopen(newNr);
                        }
                    }}
                    onCancel={() => setShowCopy(false)}
                />
            )}
            {showSend && (
                <SendMail
                    kNr={service?.kNr || 0}
                    kaNr={service?.kaNr}
                    markForExportDefault
                    markForExportLabel="Unterschriebenen Serviceauftrag versenden"
                    markAsSentDisabled={service?.status !== 0 && service?.status !== 1}
                    markAsSentLabel="Service als versendet markieren"
                    onSubmit={(kaNrListTo, kaNrListCC, markAsSent, markForExport) => {
                        if (service) {
                            sendService(service, kaNrListTo, kaNrListCC, markForExport).then(() => {
                                setShowSend(false);
                                if (markAsSent && service) {
                                    save(undefined, 2);
                                }
                            });
                        }
                    }}
                    onCancel={() => setShowSend(false)}
                />
            )}
            {showDelete && (
                <ConfirmMessage
                    title="Service löschen"
                    message={'Soll das Service ' + service?.nr + ' gelöscht werden?'}
                    onConfirm={() => {
                        if (service?.nr) {
                            deleteService(service).then(() => {
                                onClose(true);
                            });
                        }
                    }}
                    onCancel={() => setShowDelete(false)}
                />
            )}
            {showCreateAuftrag && (
                <ConfirmMessage
                    title="Auftrag erstellen"
                    message={'Soll aus dem Service ' + service?.nr + ' ein Auftrag erstellt werden?'}
                    minWidth={1000}
                    content={
                        <>
                            <ul>
                                <li>Ein Auftrag mit den gewählten Positionen aus dem Service wird erstellt.</li>
                                <li>Die neue Auftragsnummer wird beim Service hinterlegt.</li>
                                <li>
                                    Der Status des Service wird auf <strong>{ServiceStatusLabel[3]}</strong> gesetzt.
                                </li>
                                <li>Der neue Auftrag wird geöffnet.</li>
                            </ul>
                            <ServicePositionenSelect
                                servicePositionen={transferPositionen || []}
                                onChange={(newTransferPositionen) => setTransferPositionen(newTransferPositionen)}
                                withLArt
                            />
                        </>
                    }
                    onConfirm={async () => {
                        if (service?.nr && kunde.data) {
                            await addAuftragFromService(kunde.data, service, transferPositionen || []).then(
                                (result) => {
                                    history.push(createRecordUrl('auftraege', service.kNr, result.newNr));
                                },
                            );
                        }
                    }}
                    onCancel={() => {
                        setShowCreateAuftrag(false);
                        // setTransferKundeNr(null);
                        // setTransferKunde(null);
                        setTransferPositionen(null);
                    }}
                />
            )}
            {changeStatusValue !== null && (
                <ConfirmMessage
                    title="Status ändern"
                    message={
                        'Soll der Status von Service ' +
                        service?.nr +
                        ' auf "' +
                        ServiceStatusLabel[changeStatusValue] +
                        '" geändert werden?'
                    }
                    onConfirm={() => {
                        if (service?.nr) {
                            save(() => setChangeStatusValue(null), changeStatusValue);
                        }
                    }}
                    onCancel={() => setChangeStatusValue(null)}
                />
            )}
            {showAddAttachments && kunde.data && sNr !== 'neu' && (
                <AddAttachments
                    kNr={kunde.data.nr}
                    forcedSmRefType={SmRefType.service}
                    forcedSmRef={sNr}
                    onClose={(refetch) => {
                        setShowAddAttachments(false);
                        if (refetch) {
                            kunde.refetch().then();
                        }
                    }}
                />
            )}
            {showSign && kunde.data && sNr !== 'neu' && service && (
                <ServiceSign
                    service={service}
                    onClose={(refetch) => {
                        setShowSign(false);
                        if (refetch) {
                            save(undefined, 1);
                            kunde.refetch().then();
                        }
                    }}
                />
            )}
        </>
    );
};
